import { registerLocaleData } from "@angular/common";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { filter, lastValueFrom } from "rxjs";
import { PreferencesService } from "./preferences.service";

@Injectable({
  providedIn: 'root'
})
export class LocalizationService {
  private registrationMap: { [key: string]: boolean } = {};
  private dateFormatLocale: string;
  private dateFormatLocales: string[] = [];
  private useLanguagePromise: Promise<any>;

  constructor(
    private translate: TranslateService,
    private preferences: PreferencesService,
  ) {
    // Register the default language
    translate.addLangs([translate.defaultLang]);
    this.dateFormatLocales = [translate.defaultLang];
    this.useLanguagePromise = lastValueFrom(translate.use(translate.defaultLang));
    this.dateFormatLocale = '';
    this.setDateFormatLocale(translate.defaultLang);

    // Set up the preferences listener to reload stuff if required
    this.preferences.language
      .pipe(
        filter(Boolean),
      )
      .subscribe((language) => {
        // Update language
        if (language !== this.getLanguage()) {
          let targetLanguage: string = language;

          if (!this.translate.langs.includes(targetLanguage)) {
            // No stored preference or missing, try using the browser language
            targetLanguage = this.autoDetectLanguage();
          }
          this.setLanguage(targetLanguage);
        }
      });

    this.preferences.locale
      .pipe(
        filter(Boolean),
      )
      .subscribe((locale) => {
        // Update locale
        if (locale !== this.getDateFormatLocale()) {
          let targetLocale: string = locale;
          if (!this.supportsLocale(targetLocale)) {
            targetLocale = this.autoDetectLocale();
          }
          this.setDateFormatLocale(targetLocale);
        }
      });
  }

  public getLocales(): string[] {
    return this.dateFormatLocales;
  }

  public supportsLocale(locale: string): boolean {
    return this.dateFormatLocales.includes(locale);
  }

  public async setDateFormatLocale(locale: string): Promise<void> {
    this.dateFormatLocale = locale;
    if (!this.registrationMap[this.dateFormatLocale]) {
      try {
        await import(
          /* webpackExclude: /\.d\.ts$/ */
          /* webpackMode: "lazy-once" */
          /* webpackChunkName: "i18n-base" */
          `../../../../node_modules/@angular/common/locales/${this.dateFormatLocale}.mjs`).then(m => registerLocaleData(m.default, this.dateFormatLocale));
         this.registrationMap[this.dateFormatLocale] = true;
      } catch (error) {
        console.log(error);
      }
    }
  }

  public getDateFormatLocale(): string {
    return this.dateFormatLocale ?? this.translate.currentLang;
  }

  public getLanguages(): string[] {
    return this.translate.langs;
  }

  public async setLanguage(lang: string): Promise<boolean> {
    if (!this.translate.langs.includes(lang)) {
      return false;
    }

    if (this.useLanguagePromise) {
      // Wait until the previous language loading is finished to prevent race conditions
      await this.useLanguagePromise;
    }

    this.useLanguagePromise = lastValueFrom(this.translate.use(lang));
    return this.useLanguagePromise.then(() => true);
  }

  public getLanguage(): string {
    return this.translate.currentLang;
  }

  public async load(languages: string[] = [], dateFormatUrl: string): Promise<void> {
    this.translate.addLangs(languages);

    const getDateFormats: Promise<string[]> = this.loadDateFormatLocaleList(dateFormatUrl);
    // Use the auto detected values until the preferences are loaded
    await this.setLanguage(this.autoDetectLanguage());
    this.dateFormatLocales = await getDateFormats;
    await this.setDateFormatLocale(this.autoDetectLocale());
  }

  public async save(): Promise<void> {
    if (this.dateFormatLocale) {
      await this.preferences.setLocale(this.dateFormatLocale);
    }
    if (this.translate.currentLang) {
      await this.preferences.setLanguage(this.translate.currentLang);
    }
  }

  public autoDetectLanguage(): string {
    const browserLang = this.translate.getBrowserLang();
    if (browserLang && this.translate.langs.includes(browserLang)) {
      return browserLang;
    }
    return this.translate.getDefaultLang();
  }

  public autoDetectLocale(): string {
    const browserLang = this.translate.getBrowserCultureLang();
    if (browserLang && this.supportsLocale(browserLang)) {
      return browserLang;
    }
    return this.translate.getDefaultLang();
  }

  private async loadDateFormatLocaleList(path: string): Promise<string[]> {
    try {
      const response: Response = await fetch(path);
      return await response.json();
    }
    catch (error: any) {
      console.error(error);

      return this.dateFormatLocales;
    }
  }
}
